import React, { useState, useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedIcon from '@mui/icons-material/Verified';
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LockIcon from '@mui/icons-material/Lock';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import CloseIcon from '@mui/icons-material/Close';
import GoogleIcon from '@mui/icons-material/Google';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { adminService, DriverDetail } from '../services/adminService';

interface DriverRow {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  role: 'driver' | 'manager' | 'admin';
  isActive: boolean;
  isOnline: boolean;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  googleId?: string;
  bonusUnlocks: number;
  creditSummary?: {
    planName: string;
    planSlug?: string;
    used: number;
    monthlyLeadLimit: number | null;
    baseMonthlyLeadLimit?: number | null;
    walletCredits?: number;
    totalAvailable?: number | null;
    bonusUnlocks: number;
    remaining: number | null;
  };
  referralCode?: string;
  createdAt: string;
  lastLogin?: string;
}

const fetchDrivers = async (page: number, role: string, search: string, active: string) => {
  const params: Record<string, any> = { page, limit: 20 };
  if (role) params.role = role;
  if (search) params.search = search;
  if (active) params.active = active;
  const res = await api.get('/users', { params });
  return { users: res.data.users as DriverRow[], pagination: res.data.pagination };
};

// ── Tiny inline bar chart component (no external dep) ────────────────────────
const Sparkbar: React.FC<{
  data: { label: string; value: number }[];
  color?: string;
  height?: number;
  formatValue?: (v: number) => string;
}> = ({ data, color = '#1976d2', height = 80, formatValue }) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <Box display="flex" alignItems="flex-end" gap={0.4} sx={{ height, mt: 1 }}>
      {data.map((d, i) => (
        <Tooltip key={i} title={`${d.label}: ${formatValue ? formatValue(d.value) : d.value}`}>
          <Box
            sx={{
              flex: 1,
              minWidth: 2,
              height: `${(d.value / max) * 100}%`,
              minHeight: d.value > 0 ? 2 : 0,
              bgcolor: color,
              borderRadius: '2px 2px 0 0',
              opacity: d.value > 0 ? 0.85 : 0.15,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 1 },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

// ── KPI card ─────────────────────────────────────────────────────────────────
const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  color?: string;
  hint?: string;
  trend?: { value: number; suffix?: string };
}> = ({ label, value, icon, color = '#1976d2', hint, trend }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
    <CardContent sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: `${color}15`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
      {(hint || trend) && (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
          {trend !== undefined && (
            <>
              {trend.value >= 0 ? (
                <TrendingUpIcon fontSize="small" sx={{ color: 'success.main', fontSize: 16 }} />
              ) : (
                <TrendingDownIcon fontSize="small" sx={{ color: 'error.main', fontSize: 16 }} />
              )}
              <Typography
                variant="caption"
                sx={{ color: trend.value >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
              >
                {trend.value >= 0 ? '+' : ''}{trend.value}{trend.suffix || '%'}
              </Typography>
            </>
          )}
          {hint && (
            <Typography variant="caption" color="text.secondary">
              {hint}
            </Typography>
          )}
        </Stack>
      )}
    </CardContent>
  </Card>
);

// ── Driver Detail Drawer ─────────────────────────────────────────────────────
const DriverDetailDrawer: React.FC<{
  driverId: string | null;
  open: boolean;
  onClose: () => void;
}> = ({ driverId, open, onClose }) => {
  const [tab, setTab] = useState(0);
  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['driverDetail', driverId],
    queryFn: () => adminService.getDriverDetail(driverId!),
    enabled: Boolean(driverId),
  });

  const detail: DriverDetail | undefined = data;

  // Edit form state — synced from detail when it loads
  const [editForm, setEditForm] = React.useState<{
    name: string; email: string; phone: string; role: string;
    isActive: boolean; isOnline: boolean; emailVerified: boolean;
  } | null>(null);

  React.useEffect(() => {
    if (detail) {
      setEditForm({
        name: detail.driver.name,
        email: detail.driver.email,
        phone: detail.driver.phone || '',
        role: detail.driver.role,
        isActive: detail.driver.isActive,
        isOnline: detail.driver.isOnline,
        emailVerified: detail.driver.emailVerified,
      });
    }
  }, [detail]);

  const editMutation = useMutation({
    mutationFn: (payload: Parameters<typeof adminService.updateDriver>[1]) =>
      adminService.updateDriver(driverId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminDrivers'] });
      refetch();
      toast.success('Driver updated');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to update driver'),
  });

  // ── Subscription management dialogs ──────────────────────────────────────
  const [subDialog, setSubDialog] = useState<'grant' | 'extend' | 'cancel' | null>(null);
  const [grantPlanId, setGrantPlanId] = useState('');
  const [grantDays, setGrantDays] = useState('');
  const [extendDays, setExtendDays] = useState('30');
  const [cancelImmediate, setCancelImmediate] = useState(false);

  const { data: allPlans } = useQuery({
    queryKey: ['adminPlansForGrant'],
    queryFn: () => adminService.getPlans(),
    enabled: subDialog === 'grant',
  });

  const onSubActionDone = (message: string) => {
    setSubDialog(null);
    queryClient.invalidateQueries({ queryKey: ['adminDrivers'] });
    refetch();
    toast.success(message);
  };

  const grantMutation = useMutation({
    mutationFn: () => adminService.grantSubscription(driverId!, {
      planId: grantPlanId,
      durationDays: grantDays ? Number(grantDays) : null,
    }),
    onSuccess: (r) => onSubActionDone(r.message || 'Plan granted'),
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to grant plan'),
  });

  const extendMutation = useMutation({
    mutationFn: () => adminService.extendSubscription(driverId!, { days: Number(extendDays) }),
    onSuccess: (r) => onSubActionDone(r.message || 'Subscription extended'),
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to extend subscription'),
  });

  const cancelSubMutation = useMutation({
    mutationFn: () => adminService.cancelDriverSubscription(driverId!, { immediate: cancelImmediate }),
    onSuccess: (r) => onSubActionDone(r.message || 'Subscription canceled'),
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to cancel subscription'),
  });

  const usagePct = useMemo(() => {
    if (!detail) return 0;
    const { used, monthlyLeadLimit } = detail.credits;
    if (monthlyLeadLimit === null || monthlyLeadLimit === 0) return 0;
    return Math.min(100, Math.round((used / monthlyLeadLimit) * 100));
  }, [detail]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, maxWidth: '100%' } }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={700}>Driver Profile</Typography>
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => refetch()}><RefreshIcon /></IconButton>
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Stack>
        </Stack>
      </Box>

      {isLoading || !detail ? (
        <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
      ) : (
        <Box sx={{ p: 2 }}>
          {/* Header card */}
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Avatar sx={{ width: 56, height: 56, fontSize: 24, bgcolor: 'primary.main' }}>
              {detail.driver.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box flex={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={700}>{detail.driver.name}</Typography>
                {detail.driver.emailVerified && (
                  <Tooltip title="Email verified"><VerifiedIcon fontSize="small" sx={{ color: 'success.main' }} /></Tooltip>
                )}
                {detail.driver.googleId && (
                  <Tooltip title="Google linked"><GoogleIcon fontSize="small" sx={{ color: 'action.active' }} /></Tooltip>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">{detail.driver.email}</Typography>
              {detail.driver.phone && (
                <Typography variant="caption" color="text.secondary">{detail.driver.phone}</Typography>
              )}
            </Box>
            <Stack spacing={0.5} alignItems="flex-end">
              <Chip
                size="small"
                label={detail.driver.isActive ? 'Active' : 'Banned'}
                color={detail.driver.isActive ? 'success' : 'error'}
                variant="outlined"
              />
              <Chip
                size="small"
                label={detail.driver.isOnline ? 'Online' : 'Offline'}
                color={detail.driver.isOnline ? 'success' : 'default'}
              />
            </Stack>
          </Stack>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
            <Tab label="Overview" />
            <Tab label="Activity" />
            <Tab label="Credits" />
            <Tab label="Subscription" />
            <Tab label="Edit" icon={<EditIcon fontSize="small" />} iconPosition="start" />
          </Tabs>

          {tab === 0 && (
            <Stack spacing={2}>
              {/* Credit usage */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2">
                    {detail.credits.monthlyLeadLimit === 0 ? 'Wallet Credits' : "This Month's Credits"}
                  </Typography>
                  <Chip label={detail.credits.planName} size="small" color="primary" />
                </Stack>
                {detail.credits.monthlyLeadLimit === 0 ? (
                  <>
                    <Typography variant="h5" fontWeight={700}>
                      {detail.credits.walletCredits ?? detail.credits.remaining ?? 0} available
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Free plan uses wallet credits with no monthly quota.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h5" fontWeight={700}>
                      {detail.credits.used} / {detail.credits.monthlyLeadLimit === null ? '∞' : detail.credits.monthlyLeadLimit}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Base {detail.credits.baseMonthlyLeadLimit ?? '∞'} + Wallet {detail.credits.walletCredits ?? 0}
                      {detail.credits.remaining !== null && ` · ${detail.credits.remaining} remaining`}
                    </Typography>
                  </>
                )}
                {detail.credits.monthlyLeadLimit !== null && detail.credits.monthlyLeadLimit > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={usagePct}
                    color={usagePct > 90 ? 'error' : usagePct > 70 ? 'warning' : 'primary'}
                    sx={{ mt: 1, height: 8, borderRadius: 1 }}
                  />
                )}
              </Paper>

              {/* Stats grid */}
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Lifetime Unlocks</Typography>
                    <Typography variant="h6" fontWeight={700}>{detail.credits.lifetimeUnlocks}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Total Spent</Typography>
                    <Typography variant="h6" fontWeight={700}>₹{detail.payments.totalSpent.toLocaleString('en-IN')}</Typography>
                    <Typography variant="caption" color="text.secondary">{detail.payments.orders} orders</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Referrals</Typography>
                    <Typography variant="h6" fontWeight={700}>{detail.referralCount}</Typography>
                    <Typography variant="caption" color="text.secondary">Code: {detail.driver.referralCode || '—'}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Last Login</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {detail.driver.lastLogin ? new Date(detail.driver.lastLogin).toLocaleDateString('en-IN') : 'Never'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Monthly unlock trend */}
              {detail.unlocksByMonth.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" mb={1}>Unlocks (last 6 months)</Typography>
                  <Sparkbar
                    data={detail.unlocksByMonth.map((m) => ({ label: m.month, value: m.count }))}
                    color="#1976d2"
                    height={70}
                  />
                </Paper>
              )}

              {/* Account flags */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" mb={1}>Account</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                  <Chip size="small" label={detail.driver.emailVerified ? 'Email Verified' : 'Email Unverified'} color={detail.driver.emailVerified ? 'success' : 'default'} variant="outlined" />
                  <Chip size="small" label={detail.driver.twoFactorEnabled ? '2FA Enabled' : '2FA Off'} color={detail.driver.twoFactorEnabled ? 'success' : 'default'} variant="outlined" icon={<LockIcon />} />
                  <Chip size="small" label={detail.driver.googleId ? 'Google Linked' : 'Password Login'} variant="outlined" icon={<GoogleIcon />} />
                  <Chip size="small" label={detail.driver.fcmToken ? 'Push Enabled' : 'No Push'} variant="outlined" />
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">
                  Member since {new Date(detail.driver.createdAt).toLocaleDateString('en-IN')}
                </Typography>
              </Paper>
            </Stack>
          )}

          {tab === 1 && (
            <Stack spacing={2}>
              {/* Action breakdown */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" mb={1}>Last 90 days activity</Typography>
                {Object.keys(detail.activity.last90Days).length === 0 ? (
                  <Typography variant="caption" color="text.secondary">No activity recorded</Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {Object.entries(detail.activity.last90Days).map(([action, count]) => (
                      <Stack key={action} direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {action.replace(/_/g, ' ')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>{count}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" mb={1}>Recent Unlocks</Typography>
                {detail.activity.recentUnlocks.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">No unlocked rides yet</Typography>
                ) : (
                  <Stack spacing={1} divider={<Divider />}>
                    {detail.activity.recentUnlocks.map((u) => (
                      <Box key={u.unlockId}>
                        <Typography variant="body2" fontWeight={600}>
                          {u.ride?.aiAnalysis?.from || '?'} → {u.ride?.aiAnalysis?.to || '?'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(u.unlockedAt).toLocaleString('en-IN')}
                          {u.ride?.status && ` · ${u.ride.status}`}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>
          )}

          {tab === 2 && (
            <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2">Credit Transactions</Typography>
              </Box>
              {detail.creditHistory.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
                  No transactions yet
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.creditHistory.map((t) => (
                      <TableRow key={t._id}>
                        <TableCell>
                          <Chip size="small" label={t.type.replace(/_/g, ' ')} variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700} color={t.amount >= 0 ? 'success.main' : 'error.main'}>
                            {t.amount >= 0 ? '+' : ''}{t.amount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{t.reason || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(t.createdAt).toLocaleDateString('en-IN')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          )}

          {tab === 4 && editForm && (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" mb={2}>Edit profile</Typography>
                <Stack spacing={1.75}>
                  <TextField
                    label="Name"
                    size="small"
                    fullWidth
                    value={editForm.name}
                    onChange={(e) => setEditForm((prev) => prev ? { ...prev, name: e.target.value } : prev)}
                  />
                  <TextField
                    label="Email"
                    size="small"
                    fullWidth
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((prev) => prev ? { ...prev, email: e.target.value } : prev)}
                  />
                  <TextField
                    label="Phone"
                    size="small"
                    fullWidth
                    value={editForm.phone}
                    onChange={(e) => setEditForm((prev) => prev ? { ...prev, phone: e.target.value } : prev)}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" mb={0.5} display="block">Role</Typography>
                    <Select
                      size="small"
                      fullWidth
                      value={editForm.role}
                      onChange={(e) => setEditForm((prev) => prev ? { ...prev, role: e.target.value } : prev)}
                    >
                      <MenuItem value="driver">Driver</MenuItem>
                      <MenuItem value="manager">Manager</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.isActive}
                          onChange={(e) => setEditForm((prev) => prev ? { ...prev, isActive: e.target.checked } : prev)}
                          color="success"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Active account</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.emailVerified}
                          onChange={(e) => setEditForm((prev) => prev ? { ...prev, emailVerified: e.target.checked } : prev)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Email verified</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.isOnline}
                          onChange={(e) => setEditForm((prev) => prev ? { ...prev, isOnline: e.target.checked } : prev)}
                          color="success"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Online</Typography>}
                    />
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={editMutation.isLoading}
                    onClick={() => editMutation.mutate(editForm)}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {editMutation.isLoading ? 'Saving…' : 'Save changes'}
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          )}

          {tab === 3 && (
            <Stack spacing={2}>
              {detail.subscription ? (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2">Active Subscription</Typography>
                    <Stack direction="row" spacing={0.5}>
                      {(detail.subscription as any).cancelAtPeriodEnd && (
                        <Chip size="small" label="Cancels at period end" color="warning" />
                      )}
                      <Chip size="small" label={detail.subscription.status} color="success" />
                    </Stack>
                  </Stack>
                  <Typography variant="h6" fontWeight={700}>{detail.subscription.plan?.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ₹{detail.subscription.plan?.price} / {detail.subscription.plan?.billingInterval || 'monthly'}
                    {(detail.subscription as any).priceAtPurchase === 0 && ' · granted by admin'}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={0.5}>
                    <Typography variant="caption">Started: {new Date(detail.subscription.startsAt).toLocaleDateString('en-IN')}</Typography>
                    {detail.subscription.endsAt && (
                      <Typography variant="caption">Ends: {new Date(detail.subscription.endsAt).toLocaleDateString('en-IN')}</Typography>
                    )}
                  </Stack>
                </Paper>
              ) : (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">No active subscription (Free tier)</Typography>
                </Paper>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="contained" onClick={() => { setGrantPlanId(''); setGrantDays(''); setSubDialog('grant'); }}>
                  Grant plan
                </Button>
                <Button size="small" variant="outlined" disabled={!detail.subscription} onClick={() => { setExtendDays('30'); setSubDialog('extend'); }}>
                  Extend
                </Button>
                <Button size="small" variant="outlined" color="error" disabled={!detail.subscription} onClick={() => { setCancelImmediate(false); setSubDialog('cancel'); }}>
                  Cancel
                </Button>
              </Stack>

              {detail.subscriptionHistory.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" mb={1}>History</Typography>
                  <Stack spacing={1} divider={<Divider />}>
                    {detail.subscriptionHistory.map((s: any) => (
                      <Stack key={s._id} direction="row" justifyContent="space-between">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{s.planId?.name || 'Unknown plan'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(s.startsAt).toLocaleDateString('en-IN')}
                            {s.endsAt && ` → ${new Date(s.endsAt).toLocaleDateString('en-IN')}`}
                          </Typography>
                        </Box>
                        <Chip size="small" label={s.status} variant="outlined" />
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}
        </Box>
      )}

      {/* Grant plan */}
      <Dialog open={subDialog === 'grant'} onClose={() => setSubDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Grant a plan</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={0.5}>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={grantPlanId}
              onChange={(e) => setGrantPlanId(e.target.value)}
            >
              <MenuItem value="" disabled>Select plan…</MenuItem>
              {(allPlans || [])
                .filter((p) => p.isActive && p.slug !== 'free')
                .map((p) => (
                  <MenuItem key={p._id} value={p._id}>{p.name} — ₹{p.price}/{p.billingInterval}</MenuItem>
                ))}
            </Select>
            <TextField
              size="small"
              label="Duration (days)"
              type="number"
              value={grantDays}
              onChange={(e) => setGrantDays(e.target.value)}
              helperText="Leave blank to use the plan's billing interval"
            />
            <Typography variant="caption" color="warning.main">
              Replaces any current subscription. Granted plans give no proration credit later.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!grantPlanId || grantMutation.isLoading}
            onClick={() => grantMutation.mutate()}
          >
            {grantMutation.isLoading ? 'Granting…' : 'Grant plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend subscription */}
      <Dialog open={subDialog === 'extend'} onClose={() => setSubDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Extend subscription</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={0.5}>
            <TextField
              size="small"
              label="Days to add"
              type="number"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              inputProps={{ min: 1, max: 365 }}
            />
            {detail?.subscription?.endsAt && (
              <Typography variant="caption" color="text.secondary">
                Current end date: {new Date(detail.subscription.endsAt).toLocaleDateString('en-IN')}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!extendDays || Number(extendDays) < 1 || Number(extendDays) > 365 || extendMutation.isLoading}
            onClick={() => extendMutation.mutate()}
          >
            {extendMutation.isLoading ? 'Extending…' : 'Extend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel subscription */}
      <Dialog open={subDialog === 'cancel'} onClose={() => setSubDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel subscription</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} mt={0.5}>
            <FormControlLabel
              control={<Switch checked={!cancelImmediate} onChange={(e) => setCancelImmediate(!e.target.checked)} />}
              label={cancelImmediate
                ? 'Immediately — access ends now'
                : `At period end — access until ${detail?.subscription?.endsAt ? new Date(detail.subscription.endsAt).toLocaleDateString('en-IN') : 'the period ends'}`}
            />
            {cancelImmediate && (
              <Typography variant="caption" color="error.main">
                The driver loses paid access right away. Consider a refund from Billing &amp; Refunds if appropriate.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubDialog(null)}>Back</Button>
          <Button
            variant="contained"
            color="error"
            disabled={cancelSubMutation.isLoading}
            onClick={() => cancelSubMutation.mutate()}
          >
            {cancelSubMutation.isLoading ? 'Cancelling…' : cancelImmediate ? 'Cancel now' : 'Cancel at period end'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

// ── Main Drivers page ────────────────────────────────────────────────────────
const Drivers: React.FC = () => {
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState('driver');
  const [activeFilter, setActiveFilter] = useState('');
  const [search, setSearch] = useState('');
  const [creditDialogUser, setCreditDialogUser] = useState<DriverRow | null>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditAmountError, setCreditAmountError] = useState('');
  const [detailDriverId, setDetailDriverId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['adminDrivers', page, roleFilter, search, activeFilter],
    queryFn: () => fetchDrivers(page, roleFilter, search, activeFilter),
    keepPreviousData: true,
  });

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['driverAnalytics'],
    queryFn: () => adminService.getDriverAnalytics(),
    refetchInterval: 60_000,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/users/${id}/status`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminDrivers'] });
      queryClient.invalidateQueries({ queryKey: ['driverAnalytics'] });
      toast.success('Status updated');
    },
    onError: () => toast.error('Failed to update status'),
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      api.patch(`/users/${id}/role`, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminDrivers'] });
      toast.success('Role updated');
    },
    onError: () => toast.error('Failed to update role'),
  });

  const creditMutation = useMutation({
    mutationFn: ({ id, amount, reason }: { id: string; amount: number; reason: string }) =>
      api.post(`/admin/users/${id}/credits`, { amount, reason, type: 'manual_adjustment' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminDrivers'] });
      queryClient.invalidateQueries({ queryKey: ['driverDetail'] });
      toast.success('Credits adjusted');
    },
    onError: () => toast.error('Failed to adjust credits'),
  });

  const openCreditDialog = (u: DriverRow) => {
    setCreditDialogUser(u);
    setCreditAmount('');
    setCreditReason('');
    setCreditAmountError('');
  };

  const handleCreditDialogConfirm = () => {
    const amount = Number(creditAmount);
    if (!Number.isFinite(amount) || amount === 0) {
      setCreditAmountError('Enter a non-zero integer');
      return;
    }
    if (!Number.isInteger(amount)) {
      setCreditAmountError('Must be a whole number');
      return;
    }
    creditMutation.mutate(
      { id: creditDialogUser!._id, amount, reason: creditReason || 'Manual admin adjustment' },
      { onSettled: () => setCreditDialogUser(null) }
    );
  };

  const overview = analytics?.overview;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Driver Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage driver accounts, monitor activity, and adjust credits
          </Typography>
        </Box>
      </Stack>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <KpiCard
            label="Total Drivers"
            value={analyticsLoading ? '—' : overview?.totalDrivers.toLocaleString() ?? 0}
            icon={<PeopleIcon />}
            color="#1976d2"
            hint={`${overview?.bannedDrivers ?? 0} banned`}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <KpiCard
            label="Active"
            value={analyticsLoading ? '—' : overview?.activeDrivers.toLocaleString() ?? 0}
            icon={<CheckCircleIcon />}
            color="#2e7d32"
            hint={`${Math.round(((overview?.activeDrivers || 0) / (overview?.totalDrivers || 1)) * 100)}% of total`}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <KpiCard
            label="Online Now"
            value={analyticsLoading ? '—' : overview?.onlineDrivers.toLocaleString() ?? 0}
            icon={<OnlinePredictionIcon />}
            color="#0288d1"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <KpiCard
            label="New This Month"
            value={analyticsLoading ? '—' : overview?.newThisMonth.toLocaleString() ?? 0}
            icon={<TrendingUpIcon />}
            color="#9c27b0"
            trend={overview ? { value: overview.growthRate } : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <KpiCard
            label="Verified"
            value={analyticsLoading ? '—' : overview?.verifiedDrivers.toLocaleString() ?? 0}
            icon={<VerifiedIcon />}
            color="#ed6c02"
            hint={`${overview?.twoFactorDrivers ?? 0} with 2FA`}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <KpiCard
            label="Unlocks This Month"
            value={analyticsLoading ? '—' : overview?.monthUnlocks.toLocaleString() ?? 0}
            icon={<LockIcon />}
            color="#d32f2f"
            hint={`avg ${overview?.avgUnlocksPerActiveDriver ?? 0}/driver`}
          />
        </Grid>
      </Grid>

      {/* Charts row */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" fontWeight={700}>Signups — Last 30 Days</Typography>
                <Chip size="small" label={`+${overview?.newLast7Days ?? 0} this week`} color="primary" variant="outlined" />
              </Stack>
              {analyticsLoading || !analytics ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress size={24} /></Box>
              ) : (
                <Sparkbar
                  data={analytics.signupsByDay.map((d) => ({
                    label: new Date(d.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
                    value: d.count,
                  }))}
                  color="#1976d2"
                  height={120}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>Plan Distribution</Typography>
              {analyticsLoading || !analytics ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress size={24} /></Box>
              ) : analytics.planDistribution.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No active subscriptions</Typography>
              ) : (
                <Stack spacing={1.2} mt={1}>
                  {analytics.planDistribution.map((p) => {
                    const total = analytics.planDistribution.reduce((s, x) => s + x.count, 0);
                    const pct = total > 0 ? Math.round((p.count / total) * 100) : 0;
                    return (
                      <Box key={p.planSlug}>
                        <Stack direction="row" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" fontWeight={600}>
                            {p.planName} <Typography component="span" variant="caption" color="text.secondary">· ₹{p.price}</Typography>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{p.count} ({pct}%)</Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 1 }} />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Leaderboards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <EmojiEventsIcon sx={{ color: '#f9a825' }} />
                <Typography variant="subtitle2" fontWeight={700}>Top Performers (this month)</Typography>
              </Stack>
              {analyticsLoading || !analytics ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress size={24} /></Box>
              ) : analytics.topUnlockers.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No unlocks yet this month</Typography>
              ) : (
                <Stack spacing={0.5} mt={1}>
                  {analytics.topUnlockers.map((u, idx) => (
                    <Stack
                      key={u.userId}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ p: 0.75, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => setDetailDriverId(u.userId)}
                    >
                      <Typography variant="caption" fontWeight={700} sx={{ minWidth: 18, color: idx < 3 ? '#f9a825' : 'text.secondary' }}>
                        #{idx + 1}
                      </Typography>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>{u.name.charAt(0)}</Avatar>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" fontWeight={600} noWrap>{u.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{u.email}</Typography>
                      </Box>
                      <Chip size="small" label={`${u.unlocks} unlocks`} color="primary" />
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <CurrencyRupeeIcon sx={{ color: 'success.main' }} />
                <Typography variant="subtitle2" fontWeight={700}>Top Revenue Drivers</Typography>
              </Stack>
              {analyticsLoading || !analytics ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress size={24} /></Box>
              ) : analytics.topRevenue.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No payments yet</Typography>
              ) : (
                <Stack spacing={0.5} mt={1}>
                  {analytics.topRevenue.map((u, idx) => (
                    <Stack
                      key={u.userId}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ p: 0.75, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => setDetailDriverId(u.userId)}
                    >
                      <Typography variant="caption" fontWeight={700} sx={{ minWidth: 18, color: idx < 3 ? '#f9a825' : 'text.secondary' }}>
                        #{idx + 1}
                      </Typography>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>{u.name.charAt(0)}</Avatar>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" fontWeight={600} noWrap>{u.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{u.orders} orders</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        ₹{u.totalSpent.toLocaleString('en-IN')}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1}>
          <TextField
            size="small"
            placeholder="Search name, email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            sx={{ minWidth: 260 }}
          />
          <Select
            size="small"
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            displayEmpty
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All roles</MenuItem>
            <MenuItem value="driver">Drivers</MenuItem>
            <MenuItem value="manager">Managers</MenuItem>
            <MenuItem value="admin">Admins</MenuItem>
          </Select>
          <Select
            size="small"
            value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
            displayEmpty
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">Any status</MenuItem>
            <MenuItem value="true">Active only</MenuItem>
            <MenuItem value="false">Banned only</MenuItem>
          </Select>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['adminDrivers'] });
              queryClient.invalidateQueries({ queryKey: ['driverAnalytics'] });
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>

      {/* Drivers table */}
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Driver</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Verified</TableCell>
                  <TableCell align="center">Plan</TableCell>
                  <TableCell align="center">Credits</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.users.map((u) => (
                  <TableRow key={u._id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box position="relative">
                          <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                            {u.name.charAt(0).toUpperCase()}
                          </Avatar>
                          {u.isOnline && (
                            <Box sx={{
                              position: 'absolute', bottom: 0, right: 0,
                              width: 10, height: 10, bgcolor: 'success.main',
                              borderRadius: '50%', border: '2px solid white',
                            }} />
                          )}
                        </Box>
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                            {u.googleId && <GoogleIcon sx={{ fontSize: 12, color: 'text.disabled' }} />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                          {u.phone && (
                            <Typography variant="caption" color="text.secondary" display="block">{u.phone}</Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {u.role === 'admin' ? (
                        <Chip label="Admin" size="small" color="error" />
                      ) : (
                        <Select
                          size="small"
                          value={u.role}
                          onChange={(e) => roleMutation.mutate({ id: u._id, role: e.target.value })}
                          sx={{ fontSize: 12, height: 28 }}
                        >
                          <MenuItem value="driver">Driver</MenuItem>
                          <MenuItem value="manager">Manager</MenuItem>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={u.isActive ? 'Active' : 'Banned'}
                        size="small"
                        color={u.isActive ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" justifyContent="center" spacing={0.5}>
                        {u.emailVerified && (
                          <Tooltip title="Email verified"><VerifiedIcon sx={{ fontSize: 16, color: 'success.main' }} /></Tooltip>
                        )}
                        {u.twoFactorEnabled && (
                          <Tooltip title="2FA enabled"><LockIcon sx={{ fontSize: 16, color: 'primary.main' }} /></Tooltip>
                        )}
                        {!u.emailVerified && !u.twoFactorEnabled && (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {u.role === 'driver' && u.creditSummary ? (
                        <Chip label={u.creditSummary.planName} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {u.role === 'driver' && u.creditSummary ? (
                        <Box>
                          {u.creditSummary.monthlyLeadLimit === 0 ? (
                            <>
                              <Typography variant="body2" fontWeight={700}>
                                {u.creditSummary.walletCredits ?? u.creditSummary.remaining ?? 0} available
                              </Typography>
                              <Typography variant="caption" color="text.secondary">Wallet credits</Typography>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2" fontWeight={700}>
                                {u.creditSummary.used}
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {' '}/ {u.creditSummary.monthlyLeadLimit === null ? '∞' : u.creditSummary.monthlyLeadLimit}
                                </Typography>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Wallet {u.creditSummary.walletCredits ?? 0}
                              </Typography>
                            </>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(u.createdAt).toLocaleDateString('en-IN')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" justifyContent="center">
                        <Tooltip title="View profile">
                          <IconButton size="small" onClick={() => setDetailDriverId(u._id)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {u.role === 'driver' && (
                          <Tooltip title="Adjust credits">
                            <IconButton size="small" color="primary" onClick={() => openCreditDialog(u)}>
                              <AddCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {u.role !== 'admin' && (
                          <Tooltip title={u.isActive ? 'Ban user' : 'Activate user'}>
                            <IconButton
                              size="small"
                              color={u.isActive ? 'error' : 'success'}
                              onClick={() => statusMutation.mutate({ id: u._id, isActive: !u.isActive })}
                              disabled={statusMutation.isLoading}
                            >
                              {u.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {!data?.users.length && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No drivers found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {(data?.pagination.pages ?? 0) > 1 && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination
              count={data!.pagination.pages}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Credit adjustment dialog */}
      <Dialog open={Boolean(creditDialogUser)} onClose={() => setCreditDialogUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust credits — {creditDialogUser?.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} mt={1}>
            <TextField
              autoFocus
              fullWidth
              label="Amount"
              type="number"
              value={creditAmount}
              onChange={(e) => { setCreditAmount(e.target.value); setCreditAmountError(''); }}
              error={Boolean(creditAmountError)}
              helperText={creditAmountError || 'Positive to add, negative to remove (e.g. -3)'}
              InputProps={{ endAdornment: <InputAdornment position="end">unlocks</InputAdornment> }}
            />
            <TextField
              fullWidth
              label="Reason (optional)"
              value={creditReason}
              onChange={(e) => setCreditReason(e.target.value)}
              placeholder="e.g. Goodwill bonus, billing correction"
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreditDialogUser(null)} disabled={creditMutation.isLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreditDialogConfirm}
            disabled={creditMutation.isLoading || !creditAmount}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Driver detail drawer */}
      <DriverDetailDrawer
        driverId={detailDriverId}
        open={Boolean(detailDriverId)}
        onClose={() => setDetailDriverId(null)}
      />
    </Box>
  );
};

export default Drivers;
